import { cn } from "@/utils";
import { Info } from "lucide-react";
import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

/**
 * O "(i)" ao lado de um termo: um ícone que, tocado, clicado ou focado, abre
 * uma explicação curta — "o que é CET?".
 *
 * Existe porque o `Tooltip` não serve para isso: ele é o rótulo de uma linha
 * do botão só de ícone e abre só com mouse e teclado. Explicação de termo é
 * frase inteira, e no celular — onde o lojista mais usa — hover não existe.
 *
 * - **Toque e clique alternam** o balão; mouse abre ao passar; Tab abre ao
 *   focar. Esc, clique fora e rolagem fecham.
 * - **Texto de 1 a 3 frases.** Mais que isso é `Alert` na própria tela.
 * - O botão anuncia o `label` ("O que é CET?") e `aria-expanded`; o balão
 *   entra numa região `role="status"` — o leitor de tela lê a explicação
 *   quando ela abre.
 */
export interface InfoTipProps {
  /** Nome do botão para o leitor de tela, em forma de pergunta: "O que é CET?". */
  label: string;
  /** A explicação. Curta: 1 a 3 frases. */
  children: ReactNode;
  className?: string;
}

/** Folga entre o ícone e o balão. */
const RESPIRO = 6;

/** Folga para o balão não encostar na borda da janela. */
const MARGEM = 8;

export const InfoTip = ({ label, children, className }: InfoTipProps) => {
  const id = useId();
  const raizRef = useRef<HTMLSpanElement>(null);
  const botaoRef = useRef<HTMLButtonElement>(null);
  const balaoRef = useRef<HTMLSpanElement>(null);
  const [aberto, setAberto] = useState(false);
  // Como o último ponteiro chegou ao botão. Clique de mouse e toque também dão
  // foco: sem isto, o foco abria e o clique logo em seguida fechava.
  const ponteiroRef = useRef<string | null>(null);
  const [posicao, setPosicao] = useState({ x: 0, y: 0 });

  // Posição fixa, acima e centrada no ícone; abaixo quando não cabe. Fixa para
  // escapar de cartão e tabela com `overflow: hidden`.
  useLayoutEffect(() => {
    const botao = botaoRef.current;
    const balao = balaoRef.current;
    if (!aberto || !botao || !balao) return;

    const alvo = botao.getBoundingClientRect();
    const { width, height } = balao.getBoundingClientRect();
    const x = Math.min(
      Math.max(alvo.left + alvo.width / 2 - width / 2, MARGEM),
      window.innerWidth - width - MARGEM
    );
    const acima = alvo.top - height - RESPIRO;
    setPosicao({ x, y: acima < MARGEM ? alvo.bottom + RESPIRO : acima });
  }, [aberto]);

  useEffect(() => {
    if (!aberto) return;
    const fechar = () => setAberto(false);
    const aoTeclar = (event: KeyboardEvent) => {
      if (event.key === "Escape") fechar();
    };
    const aoTocarFora = (event: PointerEvent) => {
      if (!raizRef.current?.contains(event.target as Node)) fechar();
    };
    document.addEventListener("keydown", aoTeclar);
    document.addEventListener("pointerdown", aoTocarFora);
    // Balão fixo não acompanha a rolagem: fechar é melhor que flutuar solto.
    window.addEventListener("scroll", fechar, { passive: true, capture: true });
    return () => {
      document.removeEventListener("keydown", aoTeclar);
      document.removeEventListener("pointerdown", aoTocarFora);
      window.removeEventListener("scroll", fechar, { capture: true });
    };
  }, [aberto]);

  return (
    <span
      ref={raizRef}
      className={cn("inline-flex align-middle", className)}
      // Só mouse abre no hover: no toque o `pointerenter` viria antes do clique
      // e o clique fecharia o que acabou de abrir.
      onPointerEnter={event => {
        if (event.pointerType === "mouse") setAberto(true);
      }}
      onPointerLeave={event => {
        if (event.pointerType === "mouse") setAberto(false);
      }}
    >
      <button
        ref={botaoRef}
        type="button"
        aria-label={label}
        aria-expanded={aberto}
        aria-controls={id}
        onPointerDown={event => {
          ponteiroRef.current = event.pointerType;
        }}
        onClick={() => {
          // Mouse já abriu no hover: clicar confirma, não fecha. Toque (e
          // Enter/Espaço, sem ponteiro) alternam.
          if (ponteiroRef.current === "mouse") setAberto(true);
          else setAberto(atual => !atual);
          ponteiroRef.current = null;
        }}
        onFocus={() => {
          // Foco por Tab abre; foco que veio de um toque ou clique é do onClick.
          if (!ponteiroRef.current) setAberto(true);
        }}
        onBlur={() => setAberto(false)}
        className={cn(
          "text-ds-ink-600 hover:text-ds-brand-700 focus-visible:ring-ds-focus relative inline-flex size-5 cursor-pointer items-center justify-center rounded-full transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none motion-reduce:transition-none",
          // Alvo de 44px sem empurrar a linha de texto, como no CopyableText.
          "before:absolute before:-inset-3 before:content-['']",
          aberto && "text-ds-brand-700"
        )}
      >
        <Info aria-hidden="true" className="size-4" />
      </button>
      {/* A região viva fica sempre no DOM — região que nasce junto com o
          texto não é anunciada. Só o balão entra e sai. */}
      <span id={id} role="status">
        {aberto ? (
          <span
            ref={balaoRef}
            style={{ left: posicao.x, top: posicao.y }}
            className="bg-ds-ink-900 text-ds-on-solid font-ds-sans text-ds-small shadow-ds-overlay rounded-ds-md fixed z-[1200] w-max max-w-72 px-3 py-2 font-normal"
          >
            {children}
          </span>
        ) : null}
      </span>
    </span>
  );
};
